import type { ReactElement } from "react";
import * as React from "react";

import { cn } from "@/lib/utils";

import { Slot } from "./_internal/slot";

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  /** Clones this element and forwards Input's className/props (Base-UI `render` parity). */
  render?: ReactElement;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, type, render, children, ...props },
  forwardedRef,
) {
  const classes = cn(
    "bg-input/30 border-input focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:ring-destructive/40 aria-invalid:border-destructive/50 disabled:bg-input/80 h-10 max-sm:min-h-11 rounded-lg border px-3.5 py-2 text-sm transition-all duration-200 file:h-7 file:text-sm file:font-medium focus-visible:ring-2 focus-visible:shadow-sm focus-visible:shadow-ring/10 aria-invalid:ring-2 md:text-sm file:text-foreground placeholder:text-muted-foreground/70 w-full min-w-0 outline-none file:inline-flex file:border-0 file:bg-transparent disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
    className,
  );

  if (render) {
    const renderWithChildren =
      children !== undefined ? React.cloneElement(render, undefined, children) : render;
    return (
      <Slot
        ref={forwardedRef as React.Ref<HTMLElement>}
        data-slot="input"
        className={classes}
        {...props}
      >
        {renderWithChildren}
      </Slot>
    );
  }

  return <input ref={forwardedRef} type={type} data-slot="input" className={classes} {...props} />;
});

export { Input };
export type { InputProps };
